/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import {
  Icons,
  PLACEMENT,
  Tooltip,
  TOOLTIP_PLACEMENT,
} from '@web-stories-wp/design-system';
import { __, sprintf } from '@web-stories-wp/i18n';

/**
 * Internal dependencies
 */
import { useConfig, useLayout, useStory } from '../../app';
import {
  RECORDER_HORIZONTAL_INSET,
  VideoModeLayer,
} from '../canvas/dynamicCropRecorderLayer';
import {
  formatRecorderTime,
  RECORDER_PLAYBACK_RATE_OPTIONS,
  RecordingActionGroup,
  RecordingCancelButton,
  RecordingControlButton,
  RecordingControlGroup,
  RecordingControls,
  RecordingControlsRow,
  RecordingDoneButton,
  RecordingFrameStepIcon,
  RecordingPlayButton,
  RecordingShell,
  RecordingSpeed,
  RecordingSpeedWrapper,
  RecordingStage,
  RecordingTime,
  RecordingVideo,
  RecordingVideoArea,
  recordingSpeedMenuStyles,
} from '../../elements/video/dynamicCropRecorderDisplay';
import { getDynamicCropGeometry } from '../../elements/video/dynamicCrop';
import { MEDIA_VIDEO_MINIMUM_DURATION } from '../../constants';
import useVideoTrim from './useVideoTrim';

const TRIM_FRAME_RATE = 30;

const TrimStage = styled(RecordingStage)`
  background: transparent;
`;

const TrimVideo = styled(RecordingVideo)`
  object-fit: cover;
`;

const TrimControlGroup = styled(RecordingControlGroup)`
  gap: 4px;
`;

// A vertical video leaves the controls only the video's own width.
const StackedControlsRow = styled(RecordingControlsRow)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px 8px;
`;

const TrimTimecodes = styled(RecordingTime)`
  flex-basis: 100%;
  justify-content: center;
  min-height: 24px;
`;

const TrimLoopButton = styled(RecordingControlButton)`
  background: transparent;

  &[aria-pressed='true'] {
    color: ${({ theme }) => theme.colors.primary};
    background: transparent;
  }

  &:hover:not(:disabled),
  &:focus {
    background: transparent;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SELECTION_HANDLE_WIDTH = 7;

const Timeline = styled.div`
  position: relative;
  width: 100%;
  height: 24px;
  margin: 0 0 8px;
`;

// The handles sit outside the selection, so they need this room at either end.
const InsetTimeline = styled(Timeline)`
  width: calc(100% - ${SELECTION_HANDLE_WIDTH * 2}px);
  margin-right: ${SELECTION_HANDLE_WIDTH}px;
  margin-left: ${SELECTION_HANDLE_WIDTH}px;
`;

const TimelineTrack = styled.div`
  position: absolute;
  top: 10px;
  right: 0;
  left: 0;
  height: 4px;
  overflow: hidden;
  border-radius: 8px;
  background: #4c5253;
`;

const TimelineProgress = styled.div`
  width: ${({ $progress }) => `${$progress * 100}%`};
  height: 100%;
  background: ${({ theme }) => theme.colors.primary};
`;

const Selection = styled.div`
  position: absolute;
  z-index: 2;
  top: 7px;
  left: ${({ $start }) => `${$start * 100}%`};
  width: ${({ $width }) => `${$width * 100}%`};
  height: 10px;
  border-top: 2px solid ${({ theme }) => theme.colors.standard.white};
  border-bottom: 2px solid ${({ theme }) => theme.colors.standard.white};
  cursor: grab;
  touch-action: none;
  ${({ $isPassive }) => $isPassive && 'pointer-events: none;'}

  &:active {
    cursor: grabbing;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 3px;
  }
`;

const SelectionHandle = styled.span`
  position: absolute;
  top: -2px;
  ${({ $isEnd }) =>
    `${$isEnd ? 'right' : 'left'}: -${SELECTION_HANDLE_WIDTH}px;`}
  width: ${SELECTION_HANDLE_WIDTH}px;
  height: 10px;
  border-radius: ${({ $isEnd }) => ($isEnd ? '0 4px 4px 0' : '4px 0 0 4px')};
  background: ${({ theme }) => theme.colors.standard.white};

  &::after {
    position: absolute;
    top: 3px;
    left: ${({ $isEnd }) => ($isEnd ? '1px' : '4px')};
    width: 2px;
    height: 4px;
    border-radius: 3px;
    background: ${({ theme }) => theme.colors.bg.primary};
    content: '';
  }
`;

const DraggableSelectionHandle = styled(SelectionHandle)`
  z-index: 4;
  cursor: ew-resize;
  pointer-events: auto;
  touch-action: none;

  &::before {
    position: absolute;
    inset: -8px -6px;
    content: '';
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 3px;
  }
`;

const Playhead = styled.span`
  position: absolute;
  z-index: 3;
  top: 0;
  left: ${({ $progress }) => `${$progress * 100}%`};
  width: 24px;
  height: 24px;
  border: 2px solid ${({ theme }) => theme.colors.bg.primary};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.fg.primary};
  pointer-events: none;
  transform: translateX(-50%);
`;

const PlayheadInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 24px;
  margin: 0;
  opacity: 0;
  cursor: pointer;
`;

function TrimTimeline({
  currentOffset,
  endOffset,
  isFixedDuration,
  maxOffset,
  onCurrentOffsetChange,
  onEndOffsetChange,
  onStartOffsetChange,
  rangeLabel,
  startOffset,
}) {
  const timelineRef = useRef(null);
  const dragState = useRef(null);

  if (!maxOffset) {
    return null;
  }

  const maxStart = Math.max(0, maxOffset - (endOffset - startOffset));
  const selectionDuration = Math.max(0, endOffset - startOffset);
  const startRatio = startOffset / maxOffset;
  const selectionRatio = selectionDuration / maxOffset;
  const currentRatio = Math.min(Math.max(currentOffset / maxOffset, 0), 1);

  const moveSelection = (requestedStart) => {
    onStartOffsetChange(Math.min(Math.max(requestedStart, 0), maxStart));
  };

  const beginDrag = (event, offset, onMove) => {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    dragState.current = {
      clientX: event.clientX,
      offset,
      onMove,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleDragMove = (event) => {
    const timelineWidth = timelineRef.current?.getBoundingClientRect().width;
    if (!dragState.current || !timelineWidth) {
      return;
    }
    const delta =
      ((event.clientX - dragState.current.clientX) / timelineWidth) * maxOffset;
    dragState.current.onMove(dragState.current.offset + delta);
  };

  const handleDragEnd = (event) => {
    dragState.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleArrowKey = (event, offset, onMove) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    event.preventDefault();
    const direction = event.key === 'ArrowLeft' ? -1 : 1;
    onMove(offset + direction * (event.shiftKey ? 1000 : 100));
  };

  const dragHandlers = {
    onPointerMove: handleDragMove,
    onPointerUp: handleDragEnd,
    onPointerCancel: handleDragEnd,
  };

  const TimelineContainer = isFixedDuration ? Timeline : InsetTimeline;

  return (
    <TimelineContainer ref={timelineRef}>
      <TimelineTrack>
        <TimelineProgress $progress={currentRatio} />
      </TimelineTrack>
      <PlayheadInput
        type="range"
        aria-label={__('Video playhead', 'web-stories')}
        min={0}
        max={maxOffset}
        step={1000 / TRIM_FRAME_RATE}
        value={currentOffset}
        onChange={(event) => onCurrentOffsetChange(Number(event.target.value))}
      />
      {isFixedDuration ? (
        <Selection
          role="slider"
          tabIndex={0}
          aria-label={rangeLabel}
          aria-valuemin={0}
          aria-valuemax={maxStart}
          aria-valuenow={startOffset}
          $start={startRatio}
          $width={selectionRatio}
          onKeyDown={(event) =>
            handleArrowKey(event, startOffset, moveSelection)
          }
          onPointerDown={(event) =>
            beginDrag(event, startOffset, moveSelection)
          }
          {...dragHandlers}
        >
          <SelectionHandle />
          <SelectionHandle $isEnd />
        </Selection>
      ) : (
        <Selection $isPassive $start={startRatio} $width={selectionRatio}>
          <DraggableSelectionHandle
            role="slider"
            tabIndex={0}
            aria-label={__('Trim start', 'web-stories')}
            aria-valuemin={0}
            aria-valuemax={endOffset}
            aria-valuenow={startOffset}
            onKeyDown={(event) =>
              handleArrowKey(event, startOffset, onStartOffsetChange)
            }
            onPointerDown={(event) =>
              beginDrag(event, startOffset, onStartOffsetChange)
            }
            {...dragHandlers}
          />
          <DraggableSelectionHandle
            $isEnd
            role="slider"
            tabIndex={0}
            aria-label={__('Trim end', 'web-stories')}
            aria-valuemin={startOffset}
            aria-valuemax={maxOffset}
            aria-valuenow={endOffset}
            onKeyDown={(event) =>
              handleArrowKey(event, endOffset, onEndOffsetChange)
            }
            onPointerDown={(event) =>
              beginDrag(event, endOffset, onEndOffsetChange)
            }
            {...dragHandlers}
          />
        </Selection>
      )}
      <Playhead $progress={currentRatio} />
    </TimelineContainer>
  );
}

TrimTimeline.propTypes = {
  currentOffset: PropTypes.number,
  endOffset: PropTypes.number,
  isFixedDuration: PropTypes.bool,
  maxOffset: PropTypes.number,
  onCurrentOffsetChange: PropTypes.func.isRequired,
  onEndOffsetChange: PropTypes.func.isRequired,
  onStartOffsetChange: PropTypes.func.isRequired,
  rangeLabel: PropTypes.string.isRequired,
  startOffset: PropTypes.number,
};

function TrimTransportButton({
  children,
  isLoopButton = false,
  isPlayButton = false,
  label,
  ...buttonProps
}) {
  let Control = RecordingControlButton;
  if (isPlayButton) {
    Control = RecordingPlayButton;
  } else if (isLoopButton) {
    Control = TrimLoopButton;
  }

  return (
    <Tooltip hasTail isDelayed placement={TOOLTIP_PLACEMENT.TOP} title={label}>
      <Control aria-label={label} title={label} {...buttonProps}>
        {children}
      </Control>
    </Tooltip>
  );
}

TrimTransportButton.propTypes = {
  children: PropTypes.node.isRequired,
  isLoopButton: PropTypes.bool,
  isPlayButton: PropTypes.bool,
  label: PropTypes.string.isRequired,
};

function VideoTrimLayer() {
  const { sourceUrl: thumbnailSourceUrl, aspectRatio: thumbnailAspectRatio } =
    useConfig(({ animatedThumbnail }) => animatedThumbnail || {});
  const {
    cancelTrim,
    currentTime,
    endOffset,
    isAnimatedThumbnail,
    isLooping,
    isTrimMode,
    maxOffset,
    performTrim,
    setEndOffset,
    setStartOffset,
    setIsLooping,
    setVideoNode,
    startOffset,
  } = useVideoTrim(
    ({
      state: {
        currentTime,
        endOffset,
        isAnimatedThumbnail,
        isLooping,
        isTrimMode,
        maxOffset,
        startOffset,
      },
      actions: {
        cancelTrim,
        performTrim,
        setEndOffset,
        setIsLooping,
        setStartOffset,
        setVideoNode,
      },
    }) => ({
      cancelTrim,
      currentTime,
      endOffset,
      isAnimatedThumbnail,
      isLooping,
      isTrimMode,
      maxOffset,
      performTrim,
      setEndOffset,
      setIsLooping,
      setStartOffset,
      setVideoNode,
      startOffset,
    })
  );
  const { selectedElement } = useStory(({ state: { selectedElements } }) => ({
    selectedElement: selectedElements.length === 1 ? selectedElements[0] : null,
  }));
  const { viewportHeight, viewportWidth } = useLayout(
    ({ state: { viewportHeight, viewportWidth } }) => ({
      viewportHeight,
      viewportWidth,
    })
  );
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [playheadOffset, setPlayheadOffset] = useState(0);

  const isActive = Boolean(
    isTrimMode &&
      selectedElement?.type === 'video' &&
      (!isAnimatedThumbnail ||
        (thumbnailSourceUrl &&
          selectedElement?.resource?.src === thumbnailSourceUrl))
  );
  const isTrimReady = Boolean(
    Number.isFinite(startOffset) &&
      Number.isFinite(endOffset) &&
      Number.isFinite(maxOffset) &&
      maxOffset > 0
  );
  const setRef = useCallback(
    (node) => {
      videoRef.current = node;
      setVideoNode(node);
    },
    [setVideoNode]
  );

  useEffect(() => {
    const node = videoRef.current;
    if (!node) {
      return;
    }
    node.playbackRate = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    if (!isActive) {
      setIsPlaying(false);
      setIsLooping(true);
      setPlaybackRate(1);
    }
  }, [isActive, setIsLooping]);

  useEffect(() => {
    setPlayheadOffset(Number.isFinite(currentTime) ? currentTime : 0);
  }, [currentTime]);

  const seekTo = useCallback(
    (nextOffset) => {
      const node = videoRef.current;
      if (!node || !Number.isFinite(maxOffset)) {
        return;
      }
      const clampedOffset = Math.min(Math.max(nextOffset, 0), maxOffset);
      node.currentTime = clampedOffset / 1000;
      setPlayheadOffset(clampedOffset);
    },
    [maxOffset]
  );

  const pauseVideo = useCallback(() => {
    const node = videoRef.current;
    if (!node) {
      return null;
    }

    if (!node.paused) {
      node.pause();
    }
    setIsPlaying(false);
    return node;
  }, []);

  const pauseAndSeekTo = useCallback(
    (nextOffset) => {
      if (!pauseVideo()) {
        return;
      }
      seekTo(nextOffset);
    },
    [pauseVideo, seekTo]
  );

  const togglePlayback = useCallback(() => {
    const node = videoRef.current;
    if (!node || !isTrimReady) {
      return;
    }

    if (!node.paused) {
      node.pause();
      return;
    }

    const offset = node.currentTime * 1000;
    if (isLooping && (offset < startOffset || offset >= endOffset)) {
      seekTo(startOffset);
    } else if (!isLooping && offset >= maxOffset) {
      seekTo(0);
    }
    node.play()?.catch(() => setIsPlaying(false));
  }, [endOffset, isLooping, isTrimReady, maxOffset, seekTo, startOffset]);

  const toggleSelectionLoop = useCallback(() => {
    const nextIsLooping = !isLooping;
    const nodeOffset = videoRef.current?.currentTime * 1000;
    if (
      nextIsLooping &&
      Number.isFinite(nodeOffset) &&
      (nodeOffset < startOffset || nodeOffset >= endOffset)
    ) {
      pauseAndSeekTo(startOffset);
    }
    setIsLooping(nextIsLooping);
  }, [endOffset, isLooping, pauseAndSeekTo, setIsLooping, startOffset]);

  const handleFrameStep = useCallback(
    (direction) => {
      const node = pauseVideo();
      if (!isTrimReady || !node) {
        return;
      }
      const nodeOffset = Number.isFinite(node.currentTime)
        ? node.currentTime * 1000
        : playheadOffset;
      seekTo(nodeOffset + (1000 / TRIM_FRAME_RATE) * direction);
    },
    [isTrimReady, pauseVideo, playheadOffset, seekTo]
  );

  const updateSelectionStart = useCallback(
    (requestedStart, shouldSyncPlayhead = false) => {
      if (!isTrimReady) {
        return;
      }

      const selectionDuration = endOffset - startOffset;
      const nextStart = Math.min(
        Math.max(requestedStart, 0),
        maxOffset - selectionDuration
      );
      pauseVideo();
      setStartOffset(nextStart);
      if (shouldSyncPlayhead) {
        seekTo(nextStart);
      }
    },
    [
      endOffset,
      isTrimReady,
      maxOffset,
      pauseVideo,
      seekTo,
      setStartOffset,
      startOffset,
    ]
  );

  const moveSelectionFromTimeline = useCallback(
    (nextStart) => updateSelectionStart(nextStart, isLooping),
    [isLooping, updateSelectionStart]
  );

  const moveTrimStart = useCallback(
    (requestedStart) => {
      if (!isTrimReady) {
        return;
      }
      pauseVideo();
      setStartOffset(requestedStart);
      seekTo(
        Math.min(requestedStart, endOffset - MEDIA_VIDEO_MINIMUM_DURATION)
      );
    },
    [endOffset, isTrimReady, pauseVideo, seekTo, setStartOffset]
  );

  const moveTrimEnd = useCallback(
    (requestedEnd) => {
      if (!isTrimReady) {
        return;
      }
      pauseVideo();
      setEndOffset(requestedEnd);
      seekTo(
        Math.max(requestedEnd, startOffset + MEDIA_VIDEO_MINIMUM_DURATION)
      );
    },
    [isTrimReady, pauseVideo, seekTo, setEndOffset, startOffset]
  );

  const setTrimStartAtPlayhead = useCallback(() => {
    const nodeOffset = videoRef.current?.currentTime * 1000;
    if (!Number.isFinite(nodeOffset)) {
      return;
    }
    if (isAnimatedThumbnail) {
      updateSelectionStart(nodeOffset);
    } else {
      moveTrimStart(nodeOffset);
    }
  }, [isAnimatedThumbnail, moveTrimStart, updateSelectionStart]);

  const setTrimEndAtPlayhead = useCallback(() => {
    const nodeOffset = videoRef.current?.currentTime * 1000;
    if (!Number.isFinite(nodeOffset)) {
      return;
    }
    if (isAnimatedThumbnail) {
      updateSelectionStart(nodeOffset - (endOffset - startOffset));
    } else {
      moveTrimEnd(nodeOffset);
    }
  }, [
    endOffset,
    isAnimatedThumbnail,
    moveTrimEnd,
    startOffset,
    updateSelectionStart,
  ]);

  const handlePlaybackRateChange = useCallback((_, value) => {
    setPlaybackRate(Number(value));
  }, []);

  const sourceAspectRatio = useMemo(() => {
    const width = selectedElement?.resource?.width || selectedElement?.width;
    const height = selectedElement?.resource?.height || selectedElement?.height;
    return getDynamicCropGeometry(null, width, height).sourceAspectRatio;
  }, [selectedElement]);
  const previewAspectRatio =
    Number.isFinite(thumbnailAspectRatio) && thumbnailAspectRatio > 0
      ? thumbnailAspectRatio
      : sourceAspectRatio;

  if (!isActive) {
    return null;
  }

  const labels = isAnimatedThumbnail
    ? {
        range: __('Thumbnail range', 'web-stories'),
        goToStart: __('Go to thumbnail start', 'web-stories'),
        setStart: __('Start thumbnail here', 'web-stories'),
        setEnd: __('End thumbnail here', 'web-stories'),
        goToEnd: __('Go to thumbnail end', 'web-stories'),
        play: __('Play thumbnail', 'web-stories'),
        pause: __('Pause thumbnail', 'web-stories'),
        enableLoop: __('Loop thumbnail', 'web-stories'),
        disableLoop: __('Disable thumbnail loop', 'web-stories'),
      }
    : {
        range: __('Trim range', 'web-stories'),
        goToStart: __('Go to trim start', 'web-stories'),
        setStart: __('Start trim here', 'web-stories'),
        setEnd: __('End trim here', 'web-stories'),
        goToEnd: __('Go to trim end', 'web-stories'),
        play: __('Play trimmed video', 'web-stories'),
        pause: __('Pause trimmed video', 'web-stories'),
        enableLoop: __('Loop trimmed video', 'web-stories'),
        disableLoop: __('Disable trimmed video loop', 'web-stories'),
      };
  let playbackLabel = isPlaying
    ? __('Pause video', 'web-stories')
    : __('Play video', 'web-stories');
  if (isLooping) {
    playbackLabel = isPlaying ? labels.pause : labels.play;
  }

  const availableWidth = Math.max(
    viewportWidth - RECORDER_HORIZONTAL_INSET * 2,
    0
  );
  const shouldFillWidth =
    !viewportHeight || previewAspectRatio >= availableWidth / viewportHeight;
  const resource = selectedElement.resource;
  const videoSourceKey = `${resource.mimeType || ''}:${resource.src}`;

  return (
    <VideoModeLayer
      ariaLabel={__('Trim video layer', 'web-stories')}
      testId="videoTrimLayer"
      overlayTestId="videoTrimOverlay"
      isTransparent
    >
      <RecordingShell>
        <RecordingVideoArea>
          <TrimStage
            $fillWidth={shouldFillWidth}
            $sourceAspectRatio={previewAspectRatio}
          >
            <TrimVideo
              key={videoSourceKey}
              ref={setRef}
              playsInline
              preload="auto"
              poster={selectedElement.poster || resource.poster}
              muted={isAnimatedThumbnail || Boolean(resource.isMuted)}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              onTimeUpdate={(event) =>
                setPlayheadOffset(event.currentTarget.currentTime * 1000)
              }
            >
              <source src={resource.src} type={resource.mimeType} />
              <source src={resource.src} />
            </TrimVideo>
            <RecordingActionGroup>
              <RecordingCancelButton onClick={cancelTrim}>
                {__('Cancel', 'web-stories')}
              </RecordingCancelButton>
              <RecordingDoneButton
                onClick={performTrim}
                disabled={!isTrimReady}
              >
                {__('Done', 'web-stories')}
              </RecordingDoneButton>
            </RecordingActionGroup>
          </TrimStage>
        </RecordingVideoArea>
        <RecordingControls
          aria-label={__('Trim video controls', 'web-stories')}
        >
          {isTrimReady ? (
            <TrimTimeline
              currentOffset={playheadOffset}
              endOffset={endOffset}
              isFixedDuration={isAnimatedThumbnail}
              maxOffset={maxOffset}
              rangeLabel={labels.range}
              startOffset={startOffset}
              onCurrentOffsetChange={pauseAndSeekTo}
              onEndOffsetChange={moveTrimEnd}
              onStartOffsetChange={
                isAnimatedThumbnail ? moveSelectionFromTimeline : moveTrimStart
              }
            />
          ) : (
            <Timeline aria-hidden="true">
              <TimelineTrack />
            </Timeline>
          )}
          <StackedControlsRow>
            {!isAnimatedThumbnail && (
              <TrimTimecodes>
                <span>{formatRecorderTime(playheadOffset / 1000)}</span>
                <span>
                  {sprintf(
                    /* translators: %s: length of the trimmed video. */
                    __('Trimmed to %s', 'web-stories'),
                    formatRecorderTime((endOffset - startOffset) / 1000)
                  )}
                </span>
              </TrimTimecodes>
            )}
            <TrimControlGroup>
              <TrimTransportButton
                label={labels.goToStart}
                onClick={() => pauseAndSeekTo(startOffset)}
                disabled={!isTrimReady}
              >
                <Icons.TrimGoToStart aria-hidden="true" />
              </TrimTransportButton>
              <TrimTransportButton
                label={labels.setStart}
                onClick={setTrimStartAtPlayhead}
                disabled={!isTrimReady}
              >
                <Icons.TrimSetStart aria-hidden="true" />
              </TrimTransportButton>
              <TrimTransportButton
                label={__('Previous frame', 'web-stories')}
                onClick={() => handleFrameStep(-1)}
                disabled={!isTrimReady}
              >
                <RecordingFrameStepIcon
                  $direction="previous"
                  aria-hidden="true"
                >
                  <Icons.ArrowLeft />
                </RecordingFrameStepIcon>
              </TrimTransportButton>
              <TrimTransportButton
                isPlayButton
                label={playbackLabel}
                onClick={togglePlayback}
                disabled={!isTrimReady}
              >
                {isPlaying ? (
                  <Icons.PauseOutline aria-hidden="true" />
                ) : (
                  <Icons.PlayOutline aria-hidden="true" />
                )}
              </TrimTransportButton>
              <TrimTransportButton
                label={__('Next frame', 'web-stories')}
                onClick={() => handleFrameStep(1)}
                disabled={!isTrimReady}
              >
                <RecordingFrameStepIcon $direction="next" aria-hidden="true">
                  <Icons.ArrowRight />
                </RecordingFrameStepIcon>
              </TrimTransportButton>
              <TrimTransportButton
                label={labels.setEnd}
                onClick={setTrimEndAtPlayhead}
                disabled={!isTrimReady}
              >
                <Icons.TrimSetEnd aria-hidden="true" />
              </TrimTransportButton>
              <TrimTransportButton
                label={labels.goToEnd}
                onClick={() => pauseAndSeekTo(endOffset)}
                disabled={!isTrimReady}
              >
                <Icons.TrimGoToEnd aria-hidden="true" />
              </TrimTransportButton>
            </TrimControlGroup>
            <RecordingSpeedWrapper>
              <TrimTransportButton
                isLoopButton
                label={isLooping ? labels.disableLoop : labels.enableLoop}
                aria-pressed={isLooping}
                onClick={toggleSelectionLoop}
                disabled={!isTrimReady}
              >
                <Icons.ReplayCircleFilled aria-hidden="true" />
              </TrimTransportButton>
              <RecordingSpeed
                ariaLabel={__('Playback speed', 'web-stories')}
                dropDownLabel=""
                placement={PLACEMENT.TOP_END}
                popupFillWidth={false}
                popupZIndex={30}
                menuStylesOverride={recordingSpeedMenuStyles}
                options={RECORDER_PLAYBACK_RATE_OPTIONS}
                selectedValue={playbackRate}
                onMenuItemClick={handlePlaybackRateChange}
              />
            </RecordingSpeedWrapper>
          </StackedControlsRow>
        </RecordingControls>
      </RecordingShell>
    </VideoModeLayer>
  );
}

export default VideoTrimLayer;
